import { __rest } from "tslib";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { forwardRef, useEffect, useState } from 'react';
import { SectionState } from '../../store/section/SectionState';
import { PromptHeader, ReportPromptHeaderId } from '../PromptHeader';
import { ReportContextMenu } from '../ReportContextMenu';
import { ReportSections } from './internal/ReportSections';
import { HiddenSectionDialog } from '../HiddenSectionDialog';
import { BirdActionMenu } from '../BirdActionMenu';
import { ReportContextMenuState } from '../../store/reportContextMenu/ReportContextMenuState';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { CSSVars } from '../utils/cssVars';
import { useDesignTokens } from '@sas/nova-commons/theme';
import { useContextMenuHandlers } from '../hooks';
import { exposeTestId } from '../../config';
import { Breadcrumb } from '@sas/nova-commons/components/Breadcrumb';
import { ExplanationInfoDialog } from '../ExplanationInfoDialog';
import { ManageColumnsDialog } from '../ManageColumnsDialog';
import { selectPromptHeaderFlexDirection } from '../../store/selectors';
//css
import cn from './ReportContainer.css.json';
import './ReportContainer.css';
import cnHack from './BreadcrumbHack.css.json';
import './BreadcrumbHack.css';
export const ReportContainerBase = forwardRef((_a, ref) => {
    var _b, _c, _d;
    var { hideNavigation, onReportLoad, actions, contextMenuActions, headerFlexDirection } = _a, state = __rest(_a, ["hideNavigation", "onReportLoad", "actions", "contextMenuActions", "headerFlexDirection"]);
    const [hasOnLoadFired, setHasOnLoadFired] = useState(false);
    const dt = useDesignTokens();
    const tabBarTokens = (dt) => {
        var _a, _b;
        return ({
            bg: `var(${CSSVars.BackgroundColor}, ${dt.reportTokens.backgrounds.panes.bg})`,
            fontFamily: (_b = (_a = state.pageTabStyle) === null || _a === void 0 ? void 0 : _a.font) === null || _b === void 0 ? void 0 : _b.fontFamily,
        });
    };
    const sectionData = state.currentSection ? state.sections[state.currentSection] : undefined;
    const loadedRootId = sectionData ? sectionData.rootContainerId : null;
    useEffect(() => {
        if (!hasOnLoadFired && !!loadedRootId && onReportLoad) {
            onReportLoad();
            setHasOnLoadFired(true);
        }
    }, [hasOnLoadFired, loadedRootId, onReportLoad]);
    const { onContextMenu, onKeyDown } = useContextMenuHandlers((event) => {
        contextMenuActions.setReportContextMenuTarget(null, event.nativeEvent.timeStamp);
        contextMenuActions.open(event);
    });
    const currentSectionLabel = (state.currentSection && ((_b = state.sections[state.currentSection]) === null || _b === void 0 ? void 0 : _b.label)) || '';
    const linkBreadcrumb = ((_c = state.linkLabels) === null || _c === void 0 ? void 0 : _c.length) ? (_jsx(Breadcrumb, { orientation: "HORIZONTAL", className: cnHack.hack, selectCrumb: (index) => {
            const maxIndex = state.linkLabels.length - 1;
            // For page switching we queue up pages to avoid getting in a bad state
            // We have no way of queuing up link navigation
            if (state.transitioning || index > maxIndex) {
                return;
            }
            actions.notifyLinkNavigation(maxIndex - index);
        }, values: [
            {
                isSingle: true,
                selectedIndex: 0,
                values: [state.reportLabel],
            },
            ...state.linkLabels
                .map((val) => ({
                isSingle: true,
                values: [val],
                selectedIndex: 0,
            }))
                .reverse()
                .slice(1),
            {
                isSingle: true,
                selectedIndex: 0,
                values: [currentSectionLabel],
            },
        ] })) : undefined;
    const maximized = state.maximizedVisual && !state.hiddenSection;
    // Tracks hover state of report header to notify page header when it should change its background color
    const [promptHeaderHover, setPromptHeaderHover] = useState(undefined);
    if (!state.currentSection || !sectionData || !sectionData.rootContainerId) {
        // TODO: This is really a recoverable error case. Is there a better way to handle this?
        return null;
    }
    else {
        return (_jsx("div", Object.assign({ className: cn.reportContainerWrapper }, { children: _jsxs("div", Object.assign({ className: cn.reportContainerInner, onContextMenu: onContextMenu, onKeyDown: onKeyDown, style: {
                    backgroundColor: tabBarTokens(dt).bg,
                    padding: `var(${CSSVars.Padding})`,
                }, "data-testid": exposeTestId() ? 'reportContainer' : undefined, "data-reportthemename": exposeTestId() ? dt.baseReportThemeName : undefined }, { children: [linkBreadcrumb, _jsxs("div", Object.assign({ className: cn.surface, style: {
                            flexDirection: headerFlexDirection,
                        } }, { children: [_jsx(PromptHeader, { id: "reportHeader", type: "report", viewMode: !state.emptyViewEditable, tokenOverrides: tabBarTokens, hiddenNavigation: hideNavigation === true ||
                                    ((hideNavigation === undefined || hideNavigation === 'auto') &&
                                        state.sectionTabOrder.length <= 1) ||
                                    ((_d = state.sections[state.currentSection]) === null || _d === void 0 ? void 0 : _d.hidden), onHover: (hover) => setPromptHeaderHover(hover) }), _jsx("div", Object.assign({ style: {
                                    display: 'flex',
                                    flexGrow: 1,
                                    flexBasis: 0,
                                    flexDirection: 'column',
                                    overflow: 'hidden',
                                }, "data-testid": exposeTestId() ? 'reportSections' : undefined }, { children: _jsx(ReportSections, { ref: ref, onTransitionsComplete: actions.sectionSwitchComplete, hideNavigation: linkBreadcrumb || maximized ? true : hideNavigation, promptHeaderHover: promptHeaderHover, currentSection: state.currentSection, sections: state.sections || [], sectionIds: state.sectionTabOrder, onSectionChanged: (_, id) => actions.setNextSection(id), fadeTransition: state.fadeTransition, tabBarTokens: tabBarTokens, tabTokens: () => {
                                        var _a, _b;
                                        return ({
                                            fg: (_b = (_a = state.pageTabStyle) === null || _a === void 0 ? void 0 : _a.normal) === null || _b === void 0 ? void 0 : _b.fg,
                                        });
                                    }, tabsEditable: state.tabsEditable, onAddNewTab: () => {
                                        actions.addNewSection();
                                    }, onTabLabelChange: (itemIndex, item, value, type) => {
                                        actions.editTabLabel({ itemIndex, item, value, type });
                                    }, onTabMenuOpen: (event) => {
                                        actions.notifyTabMenuOpen(event);
                                    }, onTabMenuItemSelect: (event) => {
                                        actions.selectTabMenuItem(event);
                                    }, onReorderTabs: actions.notifyReorderTabs, tabDropTypes: state.tabDropTypes, onTabDrop: ({ dropIndex, droppedItem, monitor }) => actions.notifyDrop({
                                        itemIndex: dropIndex,
                                        item: droppedItem,
                                        monitor,
                                    }), onTabClose: (itemIndex) => {
                                        actions.notifyTabClose(itemIndex);
                                    } }) }))] })), _jsx(ReportContextMenu, {}), _jsx(HiddenSectionDialog, { onContextMenu: onContextMenu }), _jsx(BirdActionMenu, {}), _jsx(ExplanationInfoDialog, {}), _jsx(ManageColumnsDialog, {})] })) })));
    }
});
export const ReportContainer = connect((state) => {
    return {
        objectName: state.bird.name,
        sections: state.sections.byId,
        sectionTabOrder: state.sections.sectionTabOrder,
        currentSection: state.sections.currentSection,
        headerFlexDirection: selectPromptHeaderFlexDirection(state, ReportPromptHeaderId),
        tabsEditable: state.sections.tabsEditable,
        linkLabels: state.sections.linkLabels,
        reportLabel: state.bird.reportLabel,
        transitioning: state.sections.transitioning,
        hiddenSection: state.sections.hiddenSection,
        maximizedVisual: state.bird.maximizedVisual,
        fadeTransition: state.sections.fadeTransition,
        emptyViewEditable: state.sections.emptyViewEditable,
        tabDropTypes: state.sections.tabDropTypes,
        pageTabStyle: state.bird.pageTabStyle,
    };
}, (dispatch) => ({
    actions: bindActionCreators(SectionState.actions, dispatch),
    contextMenuActions: bindActionCreators(ReportContextMenuState.actions, dispatch),
}), undefined, { forwardRef: true })(ReportContainerBase);
